import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ShoppingCart, Trash2, Plus, Minus, ArrowRight, ShoppingBag } from 'lucide-react';
import { useCart } from '../context/CartContext';
import SEO from '../components/SEO';

const Cart = () => {
  const { cartItems, updateQuantity, removeFromCart, getCartTotal, cartCount } = useCart();

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen pt-28 pb-24 bg-dark flex items-center justify-center">
        <SEO 
          title="Shopping Cart | UAV Geo Solutions"
          description="Your shopping cart"
          url="/cart"
        />
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <ShoppingBag size={80} className="mx-auto text-gray-600 mb-6" />
          <h2 className="text-2xl font-bold text-white mb-4">Your Cart is Empty</h2>
          <p className="text-gray-400 mb-8">Looks like you haven't added any products yet.</p>
          <Link
            to="/store"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-dark font-bold rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ShoppingCart size={20} />
            Continue Shopping
          </Link>
        </motion.div>
      </div>
    );
  }

  const total = getCartTotal();

  return (
    <div className="min-h-screen pt-28 pb-24 bg-dark">
      <SEO 
        title={`Shopping Cart (${cartCount}) | UAV Geo Solutions`}
        description="Review your shopping cart"
        url="/cart"
      />

      <div className="max-w-6xl mx-auto px-4 md:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">Shopping Cart</h1>
          <p className="text-gray-400">{cartCount} item{cartCount !== 1 ? 's' : ''} in your cart</p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2 space-y-4">
            {cartItems.map((item, index) => (
              <motion.div
                key={item.product_id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="glass rounded-xl p-4 flex flex-col sm:flex-row gap-4"
                data-testid={`cart-item-${item.product_id}`}
              >
                {/* Image */}
                <Link to={`/store/product/${item.product_id}`} className="flex-shrink-0">
                  <div className="w-full sm:w-24 h-24 rounded-lg overflow-hidden bg-white/5">
                    {item.image ? (
                      <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-600">
                        <ShoppingBag size={32} />
                      </div>
                    )}
                  </div>
                </Link>

                {/* Details */}
                <div className="flex-1 min-w-0">
                  <Link 
                    to={`/store/product/${item.product_id}`}
                    className="font-semibold text-white hover:text-primary transition-colors line-clamp-1"
                  >
                    {item.name}
                  </Link>
                  <div className="mt-1">
                    {item.discount_price ? (
                      <div className="flex items-center gap-2">
                        <span className="text-primary font-bold">₹{item.discount_price.toLocaleString()}</span>
                        <span className="text-sm text-gray-500 line-through">₹{item.price.toLocaleString()}</span>
                      </div>
                    ) : (
                      <span className="text-primary font-bold">₹{item.price.toLocaleString()}</span>
                    )}
                  </div>

                  {/* Quantity & Remove */}
                  <div className="flex items-center justify-between mt-4">
                    <div className="flex items-center bg-white/5 rounded-lg">
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                        className="p-2 hover:bg-white/10 transition-colors rounded-l-lg"
                        data-testid={`decrease-qty-${item.product_id}`}
                      >
                        <Minus size={16} />
                      </button>
                      <span className="px-4 py-2 font-semibold min-w-[50px] text-center">{item.quantity}</span>
                      <button
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                        className="p-2 hover:bg-white/10 transition-colors rounded-r-lg"
                        data-testid={`increase-qty-${item.product_id}`}
                      >
                        <Plus size={16} />
                      </button>
                    </div>

                    <button
                      onClick={() => removeFromCart(item.product_id)}
                      className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                      data-testid={`remove-item-${item.product_id}`}
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>

                {/* Subtotal */}
                <div className="text-right sm:ml-4">
                  <p className="text-sm text-gray-400">Subtotal</p>
                  <p className="text-lg font-bold text-white">
                    ₹{((item.discount_price || item.price) * item.quantity).toLocaleString()}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Order Summary */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="lg:col-span-1"
          >
            <div className="glass rounded-xl p-6 sticky top-28">
              <h2 className="text-xl font-bold text-white mb-6">Order Summary</h2>
              
              <div className="space-y-4 mb-6">
                <div className="flex justify-between text-gray-400">
                  <span>Subtotal ({cartCount} items)</span>
                  <span>₹{total.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-400">
                  <span>Shipping</span>
                  <span className="text-green-400">Free</span>
                </div>
                <div className="border-t border-white/10 pt-4">
                  <div className="flex justify-between text-xl font-bold text-white">
                    <span>Total</span>
                    <span className="text-primary">₹{total.toLocaleString()}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Including all taxes</p>
                </div>
              </div>

              <Link
                to="/checkout"
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-primary text-dark font-bold rounded-lg hover:bg-primary/90 transition-colors"
                data-testid="proceed-to-checkout"
              >
                Proceed to Checkout
                <ArrowRight size={20} />
              </Link>

              <Link
                to="/store"
                className="w-full flex items-center justify-center gap-2 px-6 py-3 mt-4 border border-white/20 text-white rounded-lg hover:bg-white/5 transition-colors"
              >
                Continue Shopping
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
